/* Copyright (c) 2020-present CloudIO Inc */

import { GenericDataRecord, Row } from '@cloudio-saas/datasource-types';
import { emptyFunction, useLatest } from '@cloudio-saas/statex';
import type Chart from 'chart.js';
import { ActiveElement, ChartEvent } from 'chart.js';
import { AnnotationOptions } from 'chartjs-plugin-annotation';
import {
  ChartJSTooltipContext,
  ChartJSTooltipContextProps,
  DefaultChartJSTooltipContextProps,
  SetChartJSTooltipContext,
  useSetChartJSTooltip,
} from 'common/Context';
import { BaseProps, Chartjs } from 'componentregistry';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { useAsyncControllerFunction } from 'controller/ControllerRegistry';
import { useDeveloperMode } from 'designer/atoms';
import { useStringExpression } from 'expression/useExpression';
import { extractDevProps } from 'platform/DevProps';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Loading from 'root/Loading';
import { useStoreBusy } from 'store/useAppStore';
import { useLookupValues } from 'store/utils/utils';
import { useTheme } from 'theme/Theme';
import useTrigger from 'triggers/useTrigger';
import { isEmpty, isNotEmpty, isNull } from 'utils/isEmpty';
import useJoinRef from 'utils/useJoinRef';
import { getChartQueryProps } from './Chartjs';
import ChartjsComponent, { ClickHandlerProps } from './ChartjsComponent';
import { Box, Button, IconButton, Popover } from '@mui/material';
import Icon from 'icons/Icon';
import { readableColorFn } from 'theme/useThemeColor';

// eslint-disable-next-line @typescript-eslint/ban-types
export type DeepPartial<T> = T extends Function
  ? T
  : T extends Array<infer U>
  ? _DeepPartialArray<U>
  : // eslint-disable-next-line @typescript-eslint/ban-types
  T extends object
  ? _DeepPartialObject<T>
  : T | undefined;
type _DeepPartialArray<T> = DeepPartial<T>[];
type _DeepPartialObject<T> = { [P in keyof T]?: DeepPartial<T[P]> };

interface Props extends Required<Chartjs.Chartjs>, BaseProps<'Chartjs'> {}

const opacity = 1;

export const backgroundColors = [
  `rgba(74, 137, 220, ${opacity})`,
  `rgba(237, 85, 101, ${opacity})`,
  `rgba(153, 102, 255, ${opacity})`,
  `rgba(252, 110, 81, ${opacity})`,
  `rgba(255, 206, 84, ${opacity})`,
  `rgba(160, 212, 104, ${opacity})`,
  `rgba(72, 207, 173, ${opacity})`,
  `rgba(79, 193, 233, ${opacity})`,
  `rgba(93, 156, 236, ${opacity})`,
  `rgba(172, 146, 236, ${opacity})`,
  `rgba(236, 135, 192, ${opacity})`,
  `rgba(245, 247, 250, ${opacity})`,
  `rgba(204, 209, 217, ${opacity})`,
  `rgba(101, 109, 120, ${opacity})`,
  `rgba(218, 68, 83, ${opacity})`,
  `rgba(233, 87, 63, ${opacity})`,
  `rgba(252, 187, 66, ${opacity})`,
  `rgba(140, 193, 82, ${opacity})`,
  `rgba(55, 188, 155, ${opacity})`,
  `rgba(59, 175, 218, ${opacity})`,
  `rgba(150, 122, 220, ${opacity})`,
  `rgba(215, 112, 173, ${opacity})`,
  `rgba(230, 233, 237, ${opacity})`,
  `rgba(170, 178, 189, ${opacity})`,
];

const borderOpacity = 1;
export const borderColors = [
  `rgba(74, 137, 220, ${borderOpacity})`,
  `rgba(237, 85, 101, ${borderOpacity})`,
  `rgba(153, 102, 255, ${opacity})`,
  `rgba(252, 110, 81, ${borderOpacity})`,
  `rgba(255, 206, 84, ${borderOpacity})`,
  `rgba(160, 212, 104, ${borderOpacity})`,
  `rgba(72, 207, 173, ${borderOpacity})`,
  `rgba(79, 193, 233, ${borderOpacity})`,
  `rgba(93, 156, 236, ${borderOpacity})`,
  `rgba(172, 146, 236, ${borderOpacity})`,
  `rgba(236, 135, 192, ${borderOpacity})`,
  `rgba(245, 247, 250, ${borderOpacity})`,
  `rgba(204, 209, 217, ${borderOpacity})`,
  `rgba(101, 109, 120, ${borderOpacity})`,
  `rgba(218, 68, 83, ${borderOpacity})`,
  `rgba(233, 87, 63, ${borderOpacity})`,
  `rgba(252, 187, 66, ${borderOpacity})`,
  `rgba(140, 193, 82, ${borderOpacity})`,
  `rgba(55, 188, 155, ${borderOpacity})`,
  `rgba(59, 175, 218, ${borderOpacity})`,
  `rgba(150, 122, 220, ${borderOpacity})`,
  `rgba(215, 112, 173, ${borderOpacity})`,
  `rgba(230, 233, 237, ${borderOpacity})`,
  `rgba(170, 178, 189, ${borderOpacity})`,
];

const ds1Data = [12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
const sampleData: Chart.ChartData = {
  labels: [
    'Blue',
    'Grape Fruit',
    'Purple',
    'Bitter Sweet',
    'Sunflower',
    'Grass',
    'Mint',
    'Aqua',
    'Lavender',
    'Pink',
    'Light Gray',
    'Medium Gray',
  ],
  datasets: [
    {
      label: 'Category A',
      data: ds1Data,
      backgroundColor: ds1Data.map(
        (_, i) => backgroundColors[i % backgroundColors.length],
      ),
      borderColor: ds1Data.map((_, i) => borderColors[i % borderColors.length]),
    },
    {
      label: 'Category B',
      data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      backgroundColor: ds1Data.map(
        (_, i) => backgroundColors[i % backgroundColors.length],
      ),
      borderColor: ds1Data.map((_, i) => borderColors[i % borderColors.length]),
    },
  ],
};

interface XProps extends Props {
  records: Row<GenericDataRecord>[];
  onChartClick?: (props: ClickHandlerProps) => void;
  busy: boolean;
}

function IOChartjsWithRecords(props: XProps) {
  const {
    align,
    anchor,
    appUid,
    backgroundColor,
    borderColorPallet: borderColorPalletExpression,
    busy,
    categoryAttribute,
    categoryAttributeLookup,
    chartType,
    children,
    colorPallet: colorPalletExpression,
    dlColorPallet: dlColorPalletExpression,
    enableDatalabels,
    fill,
    functionType,
    gridLines,
    height,
    hideTooltips,
    indexAxis,
    itemId,
    labelAttribute,
    labelAttributeLookup,
    labelFormatExpression,
    offset,
    onChartClick,
    pageId,
    platform,
    preRenderFunction,
    records,
    showAvgLine,
    showBorder,
    showMaxLine,
    showMinLine,
    ticks,
    title: titleExpression,
    valueAttribute,
    width,
    xAxisLabel: xAxisLabelExpression,
    yAxisLabel: yAxisLabelExpression,
    stackedBars,
    avgLineColor,
    avgLineWidth,
    dsalias,
    skipNullValue = false,
  } = props;
  const theme = useTheme();
  const setChartJSTooltip = useSetChartJSTooltip();
  const textColor = readableColorFn(theme, backgroundColor);
  const ref = useRef<HTMLDivElement>(null);
  const [categoryLookupValues] = useLookupValues(
    platform,
    appUid,
    categoryAttributeLookup,
  );
  const [labelLookupValues] = useLookupValues(
    platform,
    appUid,
    labelAttributeLookup,
  );

  const hasCustomTooltip =
    Array.isArray(children) &&
    !!children.find((c) => c.props.itemType === 'ChartJSTooltip');

  useLayoutEffect(() => {
    setChartJSTooltip({
      ...DefaultChartJSTooltipContextProps,
      ref,
    });
  }, [setChartJSTooltip]);

  let { legendPosition, chartHeight, chartWidth } = props;

  const colorPallet = useStringExpression({
    exp: colorPalletExpression,
    context: { appUid, pageId, itemId, property: 'colorPallet', data: {} },
  });
  const dlColor =
    useStringExpression({
      exp: dlColorPalletExpression,
      context: { appUid, pageId, itemId, property: 'dlColorPallet', data: {} },
    }) ?? '';
  const avgLineColorValue =
    useStringExpression({
      exp: avgLineColor,
      context: { appUid, pageId, itemId, property: 'avgLineColor', data: {} },
    }) ?? '';
  const borderColorPallet = useStringExpression({
    exp: borderColorPalletExpression,
    context: {
      appUid,
      pageId,
      itemId,
      property: 'borderColorPallet',
      data: {},
    },
  });
  const title =
    useStringExpression({
      exp: titleExpression,
      context: { appUid, pageId, itemId, property: 'title', data: {} },
    }) ?? '';
  const xAxisLabel =
    useStringExpression({
      exp: xAxisLabelExpression,
      context: { appUid, pageId, itemId, property: 'xAxisLabel', data: {} },
    }) ?? '';
  const yAxisLabel =
    useStringExpression({
      exp: yAxisLabelExpression,
      context: { appUid, pageId, itemId, property: 'yAxisLabel', data: {} },
    }) ?? '';

  const bgColors = colorPallet ? colorPallet.split(',') : backgroundColors;
  const bgBorderColors = borderColorPallet
    ? borderColorPallet.split(',')
    : borderColors;
  const devMode = useDeveloperMode(appUid, pageId);
  let chartData: Chart.ChartData = devMode ? sampleData : { datasets: [] };
  if (records.length && valueAttribute && labelAttribute && functionType) {
    if (!categoryAttribute) {
      // single dataset
      let colorCategory: string | undefined | null = null;
      let borderColorCategory: string | undefined | null = null;

      const labels = records.map((r) => {
        if (colorCategory) {
          colorCategory =
            colorCategory +
            ',' +
            platform.evalIfExpressionString(
              appUid,
              pageId,
              itemId,
              'colorPallet',
              { recordId: r._id, dsAlias: dsalias },
              colorPalletExpression,
            );
        } else {
          colorCategory = platform.evalIfExpressionString(
            appUid,
            pageId,
            itemId,
            'colorPallet',
            { recordId: r._id, dsAlias: dsalias },
            colorPalletExpression,
          );
        }
        if (borderColorCategory) {
          borderColorCategory =
            borderColorCategory +
            ',' +
            platform.evalIfExpressionString(
              appUid,
              pageId,
              itemId,
              'borderColorPallet',
              { recordId: r._id, dsAlias: dsalias },
              borderColorPalletExpression,
            );
        } else {
          borderColorCategory = platform.evalIfExpressionString(
            appUid,
            pageId,
            itemId,
            'borderColorPallet',
            { recordId: r._id, dsAlias: dsalias },
            borderColorPalletExpression,
          );
        }
        let l = r[labelAttribute];
        if (labelLookupValues.length && !isNull(l)) {
          l = labelLookupValues.find((v) => v.value === l)?.label ?? l;
        }
        if (isNotEmpty(labelFormatExpression)) {
          l =
            platform.evalString(
              appUid,
              pageId,
              itemId,
              'labelFormatExpression',
              { label: l, record: r },
              labelFormatExpression,
            ) ?? l;
        }
        if (typeof l !== 'string') {
          l = String(l);
        }
        return l;
      }) as string[];
      const data = records.map((r) => r[valueAttribute]) as number[];
      chartData = {
        labels,
        datasets: [
          {
            // label: '# of Votes',
            data,
            // @ts-ignore records
            records,
            backgroundColor: data.map((_, index) => {
              const colorCategoryList =
                colorCategory && colorCategory.split(',');
              return colorCategoryList
                ? colorCategoryList[index % colorCategoryList.length]
                : bgColors[index % bgColors.length]
                ? bgColors[index % bgColors.length]
                : backgroundColors[index % backgroundColors.length];
            }),
            borderColor: data.map((_, index) => {
              const borderColorCategoryList =
                borderColorCategory && borderColorCategory.split(',');
              return borderColorCategoryList
                ? borderColorCategoryList[
                    index % borderColorCategoryList.length
                  ]
                : bgBorderColors[index % bgBorderColors.length]
                ? bgBorderColors[index % bgBorderColors.length]
                : borderColors[index % borderColors.length];
            }),
            tension: 0.4,
            ...(fill && { fill: 'start' }),
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBorderColor: theme.palette.background.paper,
            pointHoverBorderWidth: 1,
          },
        ],
      };
      if (['bar', 'line', 'radar'].includes(chartType)) {
        legendPosition = 'hidden';
      }
    } else {
      // multi dataset
      const labels: string[] = [];
      const categoryMap: Record<
        string,
        Record<string, { value: number; row: Record<string, unknown> }>
      > = {};

      records.forEach((r) => {
        const value = r[valueAttribute] as number;
        let label = r[labelAttribute] as string;
        if (labelLookupValues.length && !isNull(label)) {
          label =
            labelLookupValues.find((v) => v.value === label)?.label ?? label;
        }
        if (isNotEmpty(labelFormatExpression)) {
          label =
            platform.evalString(
              appUid,
              pageId,
              itemId,
              'labelFormatExpression',
              { label, record: r },
              labelFormatExpression,
            ) ?? label;
        }
        if (typeof label !== 'string') {
          label = String(label);
        }
        let category = r[categoryAttribute] as string;
        if (categoryLookupValues.length && !isNull(category)) {
          category =
            categoryLookupValues.find((l) => l.value === category)?.label ??
            category;
        }
        if (typeof category !== 'string') {
          category = String(category);
        }
        if (!labels.includes(label)) {
          labels.push(label);
        }
        let map = categoryMap[category];
        if (!map) {
          map = {};
          categoryMap[category] = map;
        }
        map[label] = { value, row: r };
      });
      let categoryColor: string | null | undefined = null;
      let categoryBorderColor: string | null | undefined = null;
      chartData = {
        labels,
        datasets: Object.keys(categoryMap).map((cat, index) => {
          const data = labels.map((lbl) => {
            const val = categoryMap[cat][lbl]?.value;
            return skipNullValue ? val ?? null : val ?? 0;
          }) as number[];
          const rows = labels.map(
            (lbl) => categoryMap[cat][lbl]?.row ?? {},
          ) as Record<string, unknown>[];
          const categorySingleRow = Object.values(categoryMap[cat])[0]?.row;
          if (categorySingleRow) {
            categoryColor = platform.evalIfExpressionString(
              appUid,
              pageId,
              itemId,
              'colorPallet',
              { recordId: categorySingleRow._id, dsAlias: dsalias },
              colorPalletExpression,
            );
            categoryBorderColor = platform.evalIfExpressionString(
              appUid,
              pageId,
              itemId,
              'borderColorPallet',
              { recordId: categorySingleRow._id, dsAlias: dsalias },
              borderColorPalletExpression,
            );
          }
          if (categoryColor && categoryColor.includes(',')) {
            const valuesArray = categoryColor
              .split(',')
              .map((val) => val.trim());
            if (
              index !== undefined &&
              index >= 0 &&
              index < valuesArray.length
            ) {
              categoryColor = valuesArray[index];
            } else {
              categoryColor = bgColors[index];
            }
          }
          if (categoryBorderColor && categoryBorderColor.includes(',')) {
            const valuesArray = categoryBorderColor
              .split(',')
              .map((val) => val.trim());
            if (
              index !== undefined &&
              index >= 0 &&
              index < valuesArray.length
            ) {
              categoryBorderColor = valuesArray[index];
            } else {
              categoryBorderColor = bgColors[index];
            }
          }

          return {
            label: cat,
            data,
            records: rows,
            backgroundColor: categoryColor
              ? categoryColor
              : bgColors[index]
              ? bgColors[index]
              : backgroundColors[index],
            borderColor: categoryBorderColor
              ? categoryBorderColor
              : bgBorderColors[index]
              ? bgBorderColors[index]
              : borderColors[index],
            tension: 0.4,
            ...(fill && { fill: 'start' }),
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBorderColor: theme.palette.background.paper,
            pointHoverBorderWidth: 1,
          };
        }),
      };
    }
  }
  const elements: DeepPartial<Chart.ElementOptionsByType<Chart.ChartType>> = {};

  switch (chartType) {
    case 'bar':
      if (showBorder) {
        elements.bar = { borderWidth: 1 };
      }
      break;
    case 'doughnut':
    case 'polarArea':
    case 'pie':
      elements.arc = { borderWidth: showBorder ? 1 : 0 };
      break;
    case 'radar':
      elements.point = { borderWidth: showBorder ? 1 : 0 };
      break;
    default:
  }

  let intersect = false;
  switch (chartType) {
    case 'doughnut':
    case 'polarArea':
    case 'pie':
      intersect = true;
      break;
    default:
      intersect = false;
  }

  const externalTooltipHandler = (context: {
    tooltip: Chart.TooltipModel<keyof Chart.ChartTypeRegistry>;
  }) => {
    const { tooltip } = context;
    if (!ref.current) {
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    let dataIndex = 0;
    const _map = tooltip.dataPoints.reduce(
      (map, { datasetIndex, dataIndex: idx }) => {
        map[`${datasetIndex}`] = true;
        dataIndex = idx;
        return map;
      },
      {} as Record<string, boolean>,
    );
    const data = chartData.datasets.map(
      // @ts-ignore records
      ({ data: _data, records: _records, label }, datasetIndex) => {
        return {
          label: chartData.labels?.[dataIndex] as string,
          // @ts-ignore records
          record: _records[dataIndex],
          category: label,
          value: (_data[dataIndex] as number) ?? 0,
          hover: _map[`${datasetIndex}`],
        };
      },
    );
    setChartJSTooltip({
      ref,
      x: tooltip.caretX + rect.left,
      y: tooltip.caretY + rect.top,
      data,
      opacity: tooltip.opacity,
    });
  };

  let options: Chart.ChartOptions = {
    indexAxis,
    elements,
    maintainAspectRatio: false,
    responsive: false,
    backgroundColor: theme.palette.background.paper,
    color: textColor,
    interaction: {
      mode: 'index',
      intersect,
    },
    onHover: onChartClick
      ? (event: ChartEvent, chartElement: ActiveElement[]) => {
          const ele = event.native?.target as HTMLCanvasElement;
          if (ele) {
            const cursor = chartElement[0] ? 'pointer' : 'default';
            if (ele.style.cursor !== cursor) {
              ele.style.cursor = cursor;
            }
          }
        }
      : undefined,
    plugins: {
      legend: {
        position: legendPosition !== 'hidden' ? legendPosition : 'bottom',
        display: legendPosition !== 'hidden',
        rtl: true,
      },
      title: {
        display: !!title,
        text: title,
      },
      tooltip: {
        rtl: true,
        mode: 'index',
        intersect,
        enabled: !(hideTooltips || hasCustomTooltip),
        position: 'nearest',
        external:
          !hideTooltips && hasCustomTooltip
            ? externalTooltipHandler
            : undefined,
        xAlign: 'center',
        yAlign: 'top',
        filter: function (tooltipItem: any) {
          return skipNullValue ? tooltipItem.raw !== null : true;
        },
      },
    },
    // @ts-ignore BarControllerChartOptions
    skipNull: skipNullValue,
    scales: {
      x: {
        display: !(
          chartType === 'doughnut' ||
          chartType === 'pie' ||
          chartType === 'polarArea'
        ),
        ticks: {
          display: ticks === 'x' || ticks === 'xy',
          color: textColor,
        },
        grid: {
          display: gridLines === 'x' || gridLines === 'xy',
          color: theme.palette.divider,
          tickColor: theme.palette.divider,
        },
        title: {
          display: !!xAxisLabel,
          text: xAxisLabel,
          color: textColor,
        },
        stacked: chartType === 'bar' && stackedBars,
      },
      y: {
        display: !(
          chartType === 'doughnut' ||
          chartType === 'pie' ||
          chartType === 'polarArea'
        ),
        ticks: {
          display: ticks === 'y' || ticks === 'xy',
          color: textColor,
        },
        grid: {
          display: gridLines === 'y' || gridLines === 'xy',
          color: theme.palette.divider,
          tickColor: theme.palette.divider,
        },
        title: {
          display: !!yAxisLabel,
          text: yAxisLabel,
          color: textColor,
        },
        stacked: chartType === 'bar' && stackedBars,
      },
    },
  };

  if (enableDatalabels) {
    options = {
      ...options,
      plugins: {
        ...options.plugins,
        datalabels: {
          anchor,
          align,
          offset,
          ...(dlColor && { color: dlColor.split(',') }),
        },
      },
    };
  }

  const data = chartData.datasets[0]?.data as number[];
  if (data?.length > 2 && (showAvgLine || showMinLine || showMaxLine)) {
    const annotations: Record<
      string,
      _DeepPartialObject<AnnotationOptions>
    > = {};

    if (showMinLine) {
      let min = Math.min(...data);
      min = Math.round(min * 100) / 100;
      annotations['min'] = {
        type: 'line',
        yMin: min,
        yMax: min,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        borderDash: [4, 4],
        label: {
          display: true,
          content: `Min: ${min} ${yAxisLabel}`,
          backgroundColor: 'transparent',
          color: theme.palette.text.secondary,
          position: 'end',
          font: {
            weight: '400',
          },
        },
      };
    }
    if (showMaxLine) {
      let max = Math.max(...data);
      max = Math.round(max * 100) / 100;
      annotations['max'] = {
        type: 'line',
        yMin: max,
        yMax: max,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        borderDash: [4, 4],
        label: {
          display: true,
          content: `Max: ${max} ${yAxisLabel}`,
          backgroundColor: 'transparent',
          color: theme.palette.text.secondary,
          position: 'end',
          font: {
            weight: '400',
          },
        },
      };
    }
    if (showAvgLine) {
      let avg = data.reduce((s, v) => s + v, 0) / data.length;
      avg = Math.round(avg * 100) / 100;
      annotations['avg'] = {
        type: 'line',
        yMin: avg,
        yMax: avg,
        borderColor: isEmpty(avgLineColorValue)
          ? theme.palette.divider
          : avgLineColorValue,
        borderWidth: avgLineWidth ?? 1,
        borderDash: [4, 4],
        label: {
          display: true,
          content: `Avg: ${avg} ${yAxisLabel}`,
          position: 'end',
          font: {
            weight: '400',
          },
        },
      };
    }

    options.plugins = {
      ...options.plugins,
      annotation: {
        clip: false,
        annotations,
      },
    };
  }

  if (chartWidth <= 1 && width) {
    chartWidth *= width;
  }
  if (chartHeight <= 1 && height) {
    chartHeight *= height;
  }

  const preRenderCallback = useAsyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: preRenderFunction,
  });

  if (busy) {
    return (
      <div
        style={{
          width: chartWidth,
          height: chartHeight,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyItems: 'center',
        }}
      >
        <Loading height={chartHeight} width={chartWidth} />
      </div>
    );
  }

  return (
    <ChartjsComponent
      ref={ref}
      appUid={appUid}
      backgroundColor={backgroundColor}
      data={chartData}
      enableDatalabels={enableDatalabels}
      height={chartHeight}
      hideTooltips={hideTooltips}
      itemId={itemId}
      onChartClick={onChartClick}
      options={options}
      pageId={pageId}
      platform={platform}
      preRenderCallback={preRenderCallback}
      type={chartType}
      width={chartWidth}
      dsalias={dsalias}
    >
      {children}
    </ChartjsComponent>
  );
}

const Comp = memo(IOChartjsWithRecords);

function IOChartjs(props: Props, ref: React.Ref<HTMLDivElement>) {
  const {
    appUid,
    backgroundColor,
    roleUid,
    categoryAttribute,
    className,
    doNotPrefetch,
    domProps,
    dsalias,
    functionType,
    itemId,
    labelAttribute,
    orderByAttribute,
    pageId,
    platform,
    valueAttribute,
    showDownloadOptions,
    fileName,
  } = props;
  const localRef = useRef<HTMLDivElement>(null);
  const joinRef = useJoinRef(ref, localRef);
  const theme = useTheme();
  const iconColor = readableColorFn(theme, backgroundColor);
  const devProps = extractDevProps(props);
  const store = dsalias
    ? platform.getStore<GenericDataRecord>(appUid, pageId, dsalias)
    : undefined;

  useEffect(() => {
    if (
      store &&
      valueAttribute &&
      labelAttribute &&
      functionType &&
      !store.transient &&
      !doNotPrefetch
    ) {
      if (!store.ignorePKDuplicate) store.ignorePKDuplicate = true;
      store.query({
        props: getChartQueryProps(
          functionType,
          valueAttribute,
          labelAttribute,
          categoryAttribute,
          orderByAttribute,
        ),
      });
    }
  }, [
    categoryAttribute,
    doNotPrefetch,
    dsalias,
    functionType,
    labelAttribute,
    orderByAttribute,
    pageId,
    platform,
    store,
    valueAttribute,
  ]);
  const [onClickTrigger, clickInProgress] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
    },
    'TriggerOnClick',
  );
  const records = useStoreRecords(store);
  const busy = useStoreBusy(store);
  const clickRef = useLatest(clickInProgress);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChartClick = useCallback(
    ({ category, label, value, record }: ClickHandlerProps) => {
      if (!onClickTrigger) return;
      if (clickRef.current) {
        platform.showWarning('Click already in progress!');
        return;
      }
      if (!store || !record) return;

      platform.startAsyncTransition(
        async () => {
          store.setCurrentRecordId(record._id);
          await onClickTrigger(undefined, {
            record: record,
            category,
            label,
            value,
          });
        },
        emptyFunction,
        { itemId, pageId, appUid },
      );
    },
    [onClickTrigger, clickRef, store, platform, itemId, pageId, appUid],
  );

  const [tooltipProps, setTooltipProps] = useState<ChartJSTooltipContextProps>(
    DefaultChartJSTooltipContextProps,
  );

  return (
    <div ref={joinRef} {...devProps} {...domProps} className={className}>
      <SetChartJSTooltipContext.Provider value={setTooltipProps}>
        <ChartJSTooltipContext.Provider value={tooltipProps}>
          {showDownloadOptions ? (
            <Box
              sx={{
                backgroundColor: 'transparent',
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'absolute',
                zIndex: 9100,
                marginRight: '16px',
                right: 0,
              }}
            >
              <IconButton aria-describedby={id} onClick={handleClick}>
                <Icon iconName="bars" iconType="regular" color={iconColor} />
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Button
                    color="inherit"
                    variant="text"
                    onClick={async () => {
                      await platform.pubSub?.pub(
                        `dwn-cjs-png-${appUid}-${pageId}-${itemId}`,
                        {
                          fileName,
                          imageType: 'jpeg',
                        },
                      );
                      setAnchorEl(null);
                    }}
                  >
                    Download JPG
                  </Button>
                  <Button
                    color="inherit"
                    variant="text"
                    onClick={async () => {
                      await platform.pubSub?.pub(
                        `dwn-cjs-png-${appUid}-${pageId}-${itemId}`,
                        {
                          fileName,
                          imageType: 'png',
                        },
                      );
                      setAnchorEl(null);
                    }}
                  >
                    Download PNG
                  </Button>
                  <Button
                    color="inherit"
                    variant="text"
                    onClick={async () => {
                      await platform.pubSub?.pub(
                        `dwn-cjs-csv-${appUid}-${pageId}-${itemId}`,
                        {
                          fileName,
                        },
                      );
                      setAnchorEl(null);
                    }}
                  >
                    Download CSV
                  </Button>
                </Box>
              </Popover>
            </Box>
          ) : (
            ''
          )}
          <Comp
            {...props}
            busy={busy}
            records={records}
            onChartClick={onClickTrigger ? handleChartClick : undefined}
          />
        </ChartJSTooltipContext.Provider>
      </SetChartJSTooltipContext.Provider>
    </div>
  );
}

export default React.forwardRef(IOChartjs);
